/**
 * Parse free-form time strings into a LocalTime object.
 * <p/>
 * When the string contains am/pm designators, they can be specified
 * as either A, P, AM, PM, and are case insensitive.
 * <p/>
 * The following operations are done on the string before parsing begins:
 * 1. trim()
 * 2. toLowerCase()
 * 3. remove all whitespace
 * <p/>
 * 24-hours formats
 * ----------------
 * H
 * HH
 * Hmm
 * HHmm
 * H:mm
 * HH:mm
 * <p/>
 * the hour portion must be 0 - 23.
 * the minute portion must be 0-59.
 * <p/>
 * AM/PM formats
 * -------------
 * H am/pm
 * HH am/pm
 * Hmm am/pm
 * HHmm am/pm
 * H:mm am/pm
 * HH:mm am/pm
 * <p/>
 * the hour portion must be 1 - 12.
 * the minute portion must be 0-59.
 */
import { LocalTime } from '@js-joda/core';

export const parseTime = (value: string): LocalTime => {
   const timeStr = value.trim().toLowerCase().replace(" ", "");

   checkValidCharactersThrowsException(timeStr);

   if (timeStr.indexOf("a") >= 0 || timeStr.indexOf("p") >= 0) {
      return handleAMPMString(timeStr);
   }

   return handle24HourTime(timeStr);
};

const separators = [':', '.'];

const validChars = ['0',
                    '1',
                    '2',
                    '3',
                    '4',
                    '5',
                    '6',
                    '7',
                    '8',
                    '9',
                    ':',
                    '.',
                    'a',
                    'p',
                    'm',
                    'A',
                    'P',
                    'M'];

const epochYear = 1970;
const epochMonth = 0;
const epochDay = 1;

const checkValidCharactersThrowsException = (timeStr: string) => {
   for (let i = 0; i < timeStr.length; i++) {
      const ch = timeStr.charAt(i);

      let found = false;

      for (let index = 0; index < validChars.length; index++) {
         if (ch === validChars[index]) {
            found = true;
            break;
         }
      }

      if (!found) {
         throw new Error(`The character '${ch}' is not allowed in a time.`);
      }
   }
};

const handleAMPMString = (timeStr: string): LocalTime => {
   const pm = timeStr.indexOf("p") >= 0;

   // strip off the am/pm portion
   if (pm) {
      timeStr = timeStr.substring(0, timeStr.indexOf("p"));
   }
   else {
      timeStr = timeStr.substring(0, timeStr.indexOf("a"));
   }

   const length = timeStr.length;
   const separatorIndex = getSeparatorIndex(timeStr);

   // string has separator, just parse the hours and minutes
   if (separatorIndex >= 0) {
      return LocalTime.of(parseHourAMPM(timeStr.substring(0, separatorIndex), pm),
                          parseMinute(timeStr.substring(separatorIndex + 1)),
                          0);
   }

   // string only contains the hour
   if (length <= 2) {
      return LocalTime.of(parseHourAMPM(timeStr, pm),
                          0,
                          0);
   }

   // string contains 1-digit hour and 2-digit minutes
   if (length === 3) {
      return LocalTime.of(parseHourAMPM(timeStr.substring(0, 1), pm),
                          parseMinute(timeStr.substring(1, 3)),
                          0);
   }

   // string contains 2-digit hour and 2-digit minutes
   if (length === 4) {
      return LocalTime.of(parseHourAMPM(timeStr.substring(0, 2), pm),
                          parseMinute(timeStr.substring(2, 4)),
                          0);
   }

   throw new Error(`'${timeStr}' is not a valid time.`);
};

const handle24HourTime = (timeStr: string): LocalTime => {
   const length = timeStr.length;
   const separatorIndex = getSeparatorIndex(timeStr);

   // string has separator, just parse the hours and minutes
   if (separatorIndex >= 0) {
      return LocalTime.of(parseHour(timeStr.substring(0, separatorIndex)),
                          parseMinute(timeStr.substring(separatorIndex + 1)),
                          0);
   }

   // string only contains the hour
   if (length <= 2) {
      return LocalTime.of(parseHour(timeStr),
                          0,
                          0);
   }

   // string contains 1-digit hour and 2-digit minutes
   if (length === 3) {
      return LocalTime.of(parseHour(timeStr.substring(0, 1)),
                          parseMinute(timeStr.substring(1, 3)),
                          0);
   }

   // string contains 2-digit hour and 2-digit minutes
   if (length === 4) {
      return LocalTime.of(parseHour(timeStr.substring(0, 2)),
                          parseMinute(timeStr.substring(2, 4)),
                          0);
   }

   throw new Error(`'${timeStr}' is not a valid time.`);
};

const parseHour = (hourStr: string): number => {
   const hour = parseInt(hourStr, 10);

   if (hour >= 0 && hour <= 23) {
      return hour;
   }

   throw new Error(`The hour '${hourStr}', must be between 0 and 23.`);
};

const parseHourAMPM = (hourStr: string, pm: boolean): number => {
   const hour = parseInt(hourStr, 10);

   if (hour < 1 || hour > 12) {
      throw new Error(`The hour '${hourStr}', must be between 1 and 12.`);
   }

   if (hour === 12) {
      // 12pm = 12 in 24-hour time
      if (pm) {
         return 12;
      }

      // 12am = 0 in 24-hour time
      return 0;
   }

   if (pm) {
      return hour + 12;
   }

   return hour;
};

const parseMinute = (minuteStr: string): number => {
   const minute = parseInt(minuteStr, 10);

   if (minute >= 0 && minute <= 59) {
      return minute;
   }

   throw new Error(`The minute '${minuteStr}' must be between 0 and 59.`);
};

const getSeparatorIndex = (timeStr: string): number => {
   for (let index = 0; index < separators.length; index++) {
      const separator = separators[index];
      const separatorIndex = timeStr.indexOf(separator);

      if (separatorIndex >= 0) {
         return separatorIndex;
      }
   }

   return -1;
};
